import React from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import CreateIngestKeyModal from '../../../components/modals/CreateIngestKeyModal'
import CopyOnceKeyModal from '../../../components/modals/CopyOnceKeyModal'
import EditIngestKeyModal from '../../../components/modals/EditIngestKeyModal'
import useIngestKeysStore from '../../../store/ingestKeys'
import type { CreateGlobalIngestKeyPayload, GlobalIngestKey, UpdateGlobalIngestKeyPayload } from '../../../types/ingest'
import { DEVTOOLS_ENABLED, debugLog } from '../../../utils/devtools'
import type { AppOutletContext } from '../../../app/AppShell'

interface CopyModalState {
  key: GlobalIngestKey
  plaintext: string
  requestId: string | null
  snippets?: {
    curl?: string
    headers?: Record<string, string>
  }
}

interface ToastState {
  open: boolean
  severity: 'success' | 'error'
  message: string
  requestId: string | null
  details?: string | null
}

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '—'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const formatHashTail = (hashTail: string): string => {
  if (!hashTail) {
    return '—'
  }
  return `…${hashTail}`
}

const formatStatus = (status: string): string => {
  return status === 'draft' ? 'Draft' : 'Published'
}

const IngestKeysPanel: React.FC = () => {
  const {
    keys,
    loading,
    error,
    fetchGlobalKeys,
    createGlobalKey,
    updateGlobalKey,
    revokeGlobalKey,
    resetError,
  } = useIngestKeysStore(
    (state) => ({
      keys: state.keys,
      loading: state.loading,
      error: state.error,
      fetchGlobalKeys: state.fetchGlobalKeys,
      createGlobalKey: state.createGlobalKey,
      updateGlobalKey: state.updateGlobalKey,
      revokeGlobalKey: state.revokeGlobalKey,
      resetError: state.resetError,
    }),
  )

  const appContext = useOutletContext<AppOutletContext>()
  const { includeSuperadmin } = appContext

  const [createOpen, setCreateOpen] = React.useState(false)
  const [copyState, setCopyState] = React.useState<CopyModalState | null>(null)
  const [editKey, setEditKey] = React.useState<GlobalIngestKey | null>(null)
  const [revokeKey, setRevokeKey] = React.useState<GlobalIngestKey | null>(null)
  const [revokeSubmitting, setRevokeSubmitting] = React.useState(false)
  const [toast, setToast] = React.useState<ToastState | null>(null)
  const [showDetails, setShowDetails] = React.useState(false)

  React.useEffect(() => {
    if (!includeSuperadmin) {
      return
    }

    let cancelled = false

    const load = async () => {
      debugLog({
        endpoint: 'ingest-keys/global',
        status: 'start',
        request_id: null,
        payload: { operation: 'list' },
      })

      try {
        const response = await fetchGlobalKeys()
        if (cancelled) {
          return
        }

        const requestId = response.request_id ?? null

        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'success',
          request_id: requestId,
          payload: { operation: 'list', count: response.keys?.length ?? 0 },
        })

        setToast({
          open: true,
          severity: 'success',
          message: `Loaded keys. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
        })
        setShowDetails(false)
      } catch (err) {
        if (cancelled) {
          return
        }

        const data = (err as { data?: unknown })?.data
        const requestId =
          data && typeof data === 'object' && 'request_id' in data
            ? String((data as { request_id?: unknown }).request_id ?? '')
            : null
        const status = (err as { status?: unknown })?.status
        const message = err instanceof Error ? err.message : 'Unable to load ingest keys.'

        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'error',
          request_id: requestId,
          payload: {
            operation: 'list',
            status: typeof status === 'number' ? status : null,
            message,
          },
        })

        setToast({
          open: true,
          severity: 'error',
          message: `Couldn't load keys. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
          details: data ? JSON.stringify(data, null, 2) : message,
        })
        setShowDetails(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [fetchGlobalKeys, includeSuperadmin])

  const handleCreate = React.useCallback(
    async (payload: CreateGlobalIngestKeyPayload) => {
      debugLog({
        endpoint: 'ingest-keys/global',
        status: 'start',
        request_id: null,
        payload: { operation: 'create', label: payload.label },
      })

      try {
        const response = await createGlobalKey(payload)
        const requestId = response.request_id ?? null
        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'success',
          request_id: requestId,
          payload: { operation: 'create', id: response.key.id },
        })

        setToast({
          open: true,
          severity: 'success',
          message: `Key created. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
        })
        setShowDetails(false)
        setCopyState({
          key: response.key,
          plaintext: response.plaintext,
          requestId,
          snippets: response.snippets,
        })
      } catch (error) {
        const data = (error as { data?: unknown })?.data
        const requestId =
          data && typeof data === 'object' && 'request_id' in data
            ? String((data as { request_id?: unknown }).request_id ?? '')
            : null
        const message = error instanceof Error ? error.message : 'Unable to create ingest key.'
        const status = (error as { status?: unknown })?.status

        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'error',
          request_id: requestId,
          payload: {
            operation: 'create',
            status: typeof status === 'number' ? status : null,
            message,
          },
        })

        setToast({
          open: true,
          severity: 'error',
          message: `Create failed. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
          details: data ? JSON.stringify(data, null, 2) : message,
        })
        setShowDetails(false)

        throw error
      }
    },
    [createGlobalKey],
  )

  const handleUpdate = React.useCallback(
    async (id: string, payload: UpdateGlobalIngestKeyPayload) => {
      debugLog({
        endpoint: 'ingest-keys/global',
        status: 'start',
        request_id: null,
        payload: { operation: 'update', id },
      })

      try {
        const response = await updateGlobalKey(id, payload)
        const requestId = response.request_id ?? null
        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'success',
          request_id: requestId,
          payload: { operation: 'update', id },
        })

        setToast({
          open: true,
          severity: 'success',
          message: `Key updated. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
        })
        setShowDetails(false)
      } catch (error) {
        const data = (error as { data?: unknown })?.data
        const requestId =
          data && typeof data === 'object' && 'request_id' in data
            ? String((data as { request_id?: unknown }).request_id ?? '')
            : null
        const message = error instanceof Error ? error.message : 'Unable to update ingest key.'
        const status = (error as { status?: unknown })?.status

        debugLog({
          endpoint: 'ingest-keys/global',
          status: 'error',
          request_id: requestId,
          payload: {
            operation: 'update',
            status: typeof status === 'number' ? status : null,
            message,
          },
        })

        setToast({
          open: true,
          severity: 'error',
          message: `Update failed. Request ID: ${requestId ?? 'unavailable'}`,
          requestId,
          details: data ? JSON.stringify(data, null, 2) : message,
        })
        setShowDetails(false)

        throw error
      }
    },
    [updateGlobalKey],
  )

  const handleRevoke = React.useCallback(async () => {
    if (!revokeKey || revokeSubmitting) {
      return
    }

    setRevokeSubmitting(true)

    debugLog({
      endpoint: 'ingest-keys/global',
      status: 'start',
      request_id: null,
      payload: { operation: 'delete', id: revokeKey.id },
    })

    try {
      const response = await revokeGlobalKey(revokeKey.id)
      const requestId = response.request_id ?? null
      debugLog({
        endpoint: 'ingest-keys/global',
        status: 'success',
        request_id: requestId,
        payload: { operation: 'delete', id: revokeKey.id },
      })

      setToast({
        open: true,
        severity: 'success',
        message: `Key revoked. Request ID: ${requestId ?? 'unavailable'}`,
        requestId,
      })
      setShowDetails(false)
      setRevokeKey(null)
    } catch (error) {
      const data = (error as { data?: unknown })?.data
      const requestId =
        data && typeof data === 'object' && 'request_id' in data
          ? String((data as { request_id?: unknown }).request_id ?? '')
          : null
      const message = error instanceof Error ? error.message : 'Unable to revoke ingest key.'
      const status = (error as { status?: unknown })?.status

      debugLog({
        endpoint: 'ingest-keys/global',
        status: 'error',
        request_id: requestId,
        payload: {
          operation: 'delete',
          status: typeof status === 'number' ? status : null,
          message,
        },
      })

      setToast({
        open: true,
        severity: 'error',
        message: `Revoke failed. Request ID: ${requestId ?? 'unavailable'}`,
        requestId,
        details: data ? JSON.stringify(data, null, 2) : message,
      })
      setShowDetails(false)
    } finally {
      setRevokeSubmitting(false)
    }
  }, [revokeGlobalKey, revokeKey, revokeSubmitting])

  const handleCloseToast = () => {
    setToast(null)
    setShowDetails(false)
  }

  const handleToggleDetails = () => {
    setShowDetails((prev) => !prev)
  }

  const handleCopyModalClose = () => {
    setCopyState(null)
  }

  const handleRevokeClose = () => {
    if (revokeSubmitting) {
      return
    }
    setRevokeKey(null)
  }

  const handleSnackbarClose = (_: unknown, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    handleCloseToast()
  }

  if (!includeSuperadmin) {
    return (
      <Stack spacing={2}>
        <Alert severity="error" variant="outlined">
          You do not have permission to manage ingest keys.
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5" component="h2">
            Ingest Keys (Global)
          </Typography>
          <Typography color="text.secondary">
            Create automation keys with copy-once plaintext. Hash tails are stored for audit.
          </Typography>
        </Box>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create new key
        </Button>
      </Stack>

      {loading ? <LinearProgress /> : null}
      {error ? (
        <Alert severity="error" onClose={() => resetError()}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>Default status</TableCell>
              <TableCell>Auto-create categories</TableCell>
              <TableCell>Rate limit (/min)</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Hash tail</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary">No ingest keys created yet.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              keys.map((key) => (
                <TableRow key={key.id} hover>
                  <TableCell>{key.label}</TableCell>
                  <TableCell>{formatStatus(key.policy.default_status)}</TableCell>
                  <TableCell>{key.policy.allow_auto_create_categories ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{key.policy.rate_limit_per_min}</TableCell>
                  <TableCell>{formatDateTime(key.created_at)}</TableCell>
                  <TableCell>{formatDateTime(key.last_used_at)}</TableCell>
                  <TableCell>{formatHashTail(key.hash_tail)}</TableCell>
                  <TableCell align="right">
                    <IconButton aria-label={`Edit ${key.label}`} onClick={() => setEditKey(key)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={`Revoke ${key.label}`}
                      color="error"
                      onClick={() => setRevokeKey(key)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <CreateIngestKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <EditIngestKeyModal
        open={Boolean(editKey)}
        ingestKey={editKey}
        onClose={() => setEditKey(null)}
        onSubmit={handleUpdate}
      />

      <CopyOnceKeyModal
        open={Boolean(copyState)}
        onClose={handleCopyModalClose}
        plaintext={copyState?.plaintext ?? null}
        label={copyState?.key.label ?? ''}
        snippets={copyState?.snippets}
      />

      <Dialog open={Boolean(revokeKey)} onClose={handleRevokeClose} fullWidth maxWidth="sm">
        <DialogTitle>Revoke ingest key</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography>
              Revoke access for <strong>{revokeKey?.label ?? 'this key'}</strong>?
            </Typography>
            <Typography color="text.secondary">
              Any automation using this key will stop working immediately. This cannot be undone.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevokeClose} color="inherit" disabled={revokeSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleRevoke} disabled={revokeSubmitting}>
            {revokeSubmitting ? 'Revoking...' : 'Revoke key'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast?.open ?? false}
        autoHideDuration={toast?.severity === 'success' ? 4000 : null}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            sx={{ width: '100%', alignItems: 'flex-start' }}
            action={
              <IconButton
                size="small"
                aria-label="close"
                color="inherit"
                onClick={handleCloseToast}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Typography component="span" variant="body2">
                {toast.message}
              </Typography>
              {DEVTOOLS_ENABLED && toast.details ? (
                <Box>
                  <Button size="small" onClick={handleToggleDetails} sx={{ px: 0 }}>
                    {showDetails ? 'Hide details' : 'Show details'}
                  </Button>
                  <Collapse in={showDetails} unmountOnExit>
                    <Box
                      component="pre"
                      sx={{
                        mt: 1,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        p: 1.5,
                        maxHeight: 200,
                        overflow: 'auto',
                        fontFamily: 'monospace',
                        fontSize: '0.8rem',
                      }}
                    >
                      {toast.details}
                    </Box>
                  </Collapse>
                </Box>
              ) : null}
            </Stack>
          </Alert>
        ) : null}
      </Snackbar>
    </Stack>
  )
}

export default IngestKeysPanel
